import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import HeroSlider from './HeroSlider';
import HeroContent from './HeroContent';
import DevisForm from '../shared/DevisForm';

const slides = [
  {
    image: "https://www.camaret.org/uploads/thumbnails/image/Aout-2022-2-1_1000x1000.webp",
    title: "Expert en diagnostics immobiliers à Camaret-sur-Aigues",
    description: "DPE, Amiante, Électricité, Gaz, Termites et tous les diagnostics obligatoires"
  },
  {
    image: "https://i.postimg.cc/Gpz9GWvQ/image2-hero.jpg",
    title: "Rapports détaillés et professionnels",
    description: "Disponibilité et réactivité pour tous vos projets immobiliers"
  },
  {
    image: "https://www.cast-things.com/images/photos/annonces/2025/01/20250109-33123-67804b2250121.jpg",
    title: "Diagnostiqueur certifié à votre service",
    description: "Intervention rapide pour ventes et locations dans toute la région"
  }
];

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 1
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 1
  })
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

const Hero = () => {
  const [[page, direction], setPage] = useState([0, 0]);
  const [isMobileFormOpen, setIsMobileFormOpen] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    clientType: 'particulier',
    propertyType: '',
    diagnostics: [],
    name: '',
    email: '',
    phone: '',
    address: '',
    constructionYear: '',
    surface: ''
  });
  const imageIndex = Math.abs(page % slides.length);

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 12000);

    return () => clearInterval(timer);
  }, [page]);

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDiagnosticToggle = (diagnostic: string) => {
    setFormData(prev => ({
      ...prev,
      diagnostics: prev.diagnostics.includes(diagnostic)
        ? prev.diagnostics.filter(d => d !== diagnostic)
        : [...prev.diagnostics, diagnostic]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsMobileFormOpen(false);
  };

  return (
    <section className="relative h-screen">
      <div className="absolute inset-0 z-0">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <HeroSlider
            key={page}
            currentSlide={imageIndex}
            direction={direction}
            slides={slides}
            variants={slideVariants}
            setPage={setPage}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
          />
        </AnimatePresence>
      </div>
      
      <div className="relative h-full z-10">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <HeroContent currentSlide={imageIndex} slides={slides} />
          <div className="hidden lg:block w-[380px] z-30 mt-32 self-start">
            <DevisForm 
              variant="hero" 
              formStep={formStep}
              formData={formData}
              onStepChange={setFormStep}
              onFormChange={handleFormChange}
              onDiagnosticToggle={handleDiagnosticToggle}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>

      {/* Bouton flottant mobile */}
      <motion.button
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        onClick={() => setIsMobileFormOpen(true)}
        className="lg:hidden fixed left-1/2 -translate-x-1/2 bottom-6 z-50 bg-dk-blue text-white rounded-full px-5 py-2.5 text-sm shadow-lg flex items-center"
      >
        Demander un devis
      </motion.button>

      {/* Mobile Form */}
      <DevisForm
        variant="modal"
        isOpen={isMobileFormOpen}
        onClose={() => setIsMobileFormOpen(false)}
        formStep={formStep}
        formData={formData}
        onStepChange={setFormStep}
        onFormChange={handleFormChange}
        onDiagnosticToggle={handleDiagnosticToggle}
        onSubmit={handleSubmit}
      />
    </section>
  );
};

export default Hero;